import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { extractApiError } from '~/utils/extract-api-error';

import { updateEnrichedAttribute } from '../services/enriched-attribute.service';

/** One compare-grid cell edit submitted by the operator. */
export interface UpdateEnrichedAttributeVariables {
  /** Curation feed_product_id — the id the PATCH endpoint is keyed by. */
  productId: string;
  /** snake_case wire field name, e.g. `product_title`. */
  field: string;
  /** Human label, used only for the toast copy. */
  label: string;
  /** Value in the attribute's own JSON shape. */
  value: unknown;
}

/**
 * Persists one compare-grid cell edit to the shared catalog-ops attribute
 * endpoint. Per-cell rather than batched: each commit is its own mutation, so
 * the calling cell shows a scoped spinner and keeps its draft on failure.
 * `connectorCode` is fixed per flow (`META` for the scheduled flow, undefined →
 * `GMC`), so it is a hook argument rather than a per-call variable.
 */
export function useUpdateEnrichedAttribute(connectorCode?: string) {
  return useMutation({
    mutationFn: ({ productId, field, value }: UpdateEnrichedAttributeVariables) =>
      updateEnrichedAttribute(productId, {
        field,
        value,
        ...(connectorCode !== undefined && { connectorCode }),
      }),
    onSuccess: (_data, variables) => {
      toast.success(`Saved ${variables.label}.`);
    },
    onError: (error) => {
      toast.error(extractApiError(error, 'Could not save the attribute. Please try again.'));
    },
  });
}
